import { Intervention } from '@/types';
import { Outlet } from 'react-router-dom';
import { isNil, update, set } from 'lodash/fp';
import { useState, useCallback } from 'react';
import { LocalScenario } from '@/types';
import { Container, Frame, Content } from '@kinesis/bungle';
import { ScenarioSidebar } from '@/components/scenario-sidebar';
import { useGetScenarioQuery, useSaveScenarioMutation } from '@/api/scenarios';
import { useGetInventoryQuery } from '@/api/inventories';
import { useScenarioId } from '@/hooks/use-scenario-id';
import { useInventoryId } from '@/hooks/use-inventory-id';

type UpdateLocalScenario = (f: LocalScenario) => LocalScenario;

const Scenario = () => {
  const inventoryId = useInventoryId();
  const scenarioId = useScenarioId();
  const { data: inventory } = useGetInventoryQuery(inventoryId);
  const { data: savedScenario } = useGetScenarioQuery(scenarioId);

  const [localScenario, setLocalScenario] = useState<LocalScenario | undefined>(
    undefined,
  );
  const updateScenario = useCallback(
    (f: UpdateLocalScenario) => {
      setLocalScenario((current) =>
        isNil(current) ? f(savedScenario as LocalScenario) : f(current),
      );
    },
    [savedScenario, setLocalScenario],
  );
  const addIntervention = useCallback(
    (i: Intervention) => {
      updateScenario(update('interventions', (v) => v.concat([i])));
    },
    [updateScenario],
  );

  const updateIntervention = useCallback(
    (id: number | string, f: (x: Intervention) => Intervention) => {
      updateScenario(
        update('interventions', (is) => {
          return is.map((i: any) => {
            if (i.id === id) {
              return f(i);
            } else {
              return i;
            }
          });
        }),
      );
    },
    [updateScenario],
  );
  const removeIntervention = useCallback(
    (k: string | number) => {
      updateScenario(
        update('interventions', (vs) => vs.filter((v: any) => v.id !== k)),
      );
    },
    [updateScenario],
  );
  const onRename = useCallback(
    (v: String) => {
      updateScenario(set('name', v));
    },
    [updateScenario],
  );

  const onDiscard = useCallback(() => {
    setLocalScenario(undefined);
  }, [setLocalScenario]);

  const [saveScenario] = useSaveScenarioMutation();

  const onSave = useCallback(() => {
    setLocalScenario((current) => {
      saveScenario(current as LocalScenario);
      return undefined;
    });
  }, [saveScenario]);

  const scenario = localScenario ?? savedScenario;
  const isDirty = !isNil(localScenario);

  return (
    <Container>
      <Frame sizing='fit-content' borderRight>
        <Content padding='none' background='white' height='100%' width={496}>
          {scenario && (
            <ScenarioSidebar
              location={inventory?.location ?? 'New location'}
              scenario={scenario}
              isDirty={isDirty}
              onRename={onRename}
              onUpdateIntervention={updateIntervention}
              onDiscard={onDiscard}
              onSave={onSave}
            />
          )}
        </Content>
      </Frame>
      <Frame sizing='fill-container'>
        {scenario && (
          <Outlet
            context={{
              scenario,
              addIntervention,
              removeIntervention,
              updateIntervention,
            }}
          />
        )}
      </Frame>
    </Container>
  );
};

export { Scenario };
